import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Calculator, Package, MapPin, Truck, ArrowRight, Phone, Mail, CheckCircle, Send } from 'lucide-react';

const Cotizacion = () => {
  const [formData, setFormData] = useState({
    tipoServicio: '',
    origen: '',
    destino: '',
    peso: '',
    dimensiones: '',
    tipoMercancia: '',
    valor: '',
    fechaEnvio: '',
    servicioAdicional: [],
    nombre: '',
    empresa: '',
    email: '',
    telefono: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [enviado, setEnviado] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      if (checked) {
        setFormData({
          ...formData,
          servicioAdicional: [...formData.servicioAdicional, value]
        });
      } else {
        setFormData({
          ...formData,
          servicioAdicional: formData.servicioAdicional.filter(item => item !== value)
        });
      }
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    // Crear el contenido del email
    const emailContent = `
NUEVA SOLICITUD DE COTIZACIÓN - REZCOM INTERNATIONAL

=== INFORMACIÓN DEL CLIENTE ===
Nombre: ${formData.nombre}
Empresa: ${formData.empresa || 'No especificada'}
Email: ${formData.email}
Teléfono: ${formData.telefono}

=== DETALLES DEL ENVÍO ===
Tipo de Servicio: ${formData.tipoServicio}
Fecha de Envío: ${formData.fechaEnvio}
Origen: ${formData.origen}
Destino: ${formData.destino}

=== INFORMACIÓN DE LA MERCANCÍA ===
Peso: ${formData.peso} kg
Dimensiones: ${formData.dimensiones} cm
Tipo de Mercancía: ${formData.tipoMercancia}
Valor Declarado: $${formData.valor} USD

=== SERVICIOS ADICIONALES ===
${formData.servicioAdicional.length > 0 
  ? formData.servicioAdicional.map(servicio => `• ${servicio}`).join('\n')
  : 'Ninguno seleccionado'
}

=== DATOS ADICIONALES ===
Fecha de Solicitud: ${new Date().toLocaleString('es-MX')}
Formulario: Página Web REZCOM

---
Esta cotización fue enviada desde el formulario web de REZCOM International.
Para responder al cliente, utiliza el email: ${formData.email}
    `.trim();

    try {
      // Crear un enlace mailto con todos los datos
      const subject = `Nueva Cotización - ${formData.nombre} - ${formData.tipoServicio}`;
      const body = encodeURIComponent(emailContent);
      const mailtoLink = `mailto:contacto@rezcom-intl.mx?subject=${encodeURIComponent(subject)}&body=${body}`;

      // Abrir cliente de email del usuario
      window.location.href = mailtoLink;

      // También enviar por WhatsApp como respaldo
      const whatsappMessage = `*NUEVA COTIZACIÓN REZCOM* 📦

*Cliente:* ${formData.nombre}
*Empresa:* ${formData.empresa || 'No especificada'}
*Email:* ${formData.email}
*Teléfono:* ${formData.telefono}

*Servicio:* ${formData.tipoServicio}
*Ruta:* ${formData.origen} → ${formData.destino}
*Peso:* ${formData.peso} kg
*Fecha:* ${formData.fechaEnvio}

¿Podrían enviarme una cotización para este servicio?`;

      const whatsappNumber = "5215562152214";
      const whatsappURL = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`;

      // Dar tiempo para que se abra el email y luego mostrar opción de WhatsApp
      setTimeout(() => {
        const useWhatsApp = window.confirm(
          "✅ Tu cotización se ha enviado por email.\n\n¿También quieres enviarla por WhatsApp para respuesta más rápida?"
        );
        if (useWhatsApp) {
          window.open(whatsappURL, '_blank');
        }
      }, 1000);

      setEnviado(true);
      
    } catch (error) {
      console.error('Error al enviar:', error);
      alert('Hubo un problema al enviar la cotización. Por favor, inténtalo de nuevo o contacta directamente por WhatsApp.');
    } finally {
      setIsLoading(false);
    }
  };

  const tiposServicio = [
    { value: 'terrestre', label: 'Transporte Terrestre' },
    { value: 'aereo', label: 'Carga Aérea' },
    { value: 'maritimo', label: 'Carga Marítima' },
    { value: 'multimodal', label: 'Transporte Multimodal' }
  ];

  const serviciosAdicionales = [
    { value: 'seguro', label: 'Seguro de Mercancía' },
    { value: 'embalaje', label: 'Embalaje Especializado' },
    { value: 'almacen', label: 'Almacenamiento Temporal' },
    { value: 'entrega', label: 'Entrega Puerta a Puerta' },
    { value: 'tracking', label: 'Seguimiento en Tiempo Real' },
    { value: 'urgente', label: 'Servicio Express' }
  ];

  if (enviado) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center py-12 px-4">
        <motion.div
          className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center"
          initial={{ scale: 0, rotate: -10 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ duration: 0.6, type: "spring" }}
        >
          <motion.div
            className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            <CheckCircle className="w-10 h-10 text-green-600" />
          </motion.div>
          
          <h2 className="text-2xl font-bold text-gray-900 mb-4">¡Cotización Enviada!</h2>
          <p className="text-gray-600 mb-6">
            Tu solicitud ha sido enviada exitosamente a nuestro equipo. Te contactaremos pronto con tu cotización personalizada.
          </p>
          
          <div className="space-y-3">
            <button
              onClick={() => {
                setEnviado(false);
                setFormData({
                  tipoServicio: '',
                  origen: '',
                  destino: '',
                  peso: '',
                  dimensiones: '',
                  tipoMercancia: '',
                  valor: '',
                  fechaEnvio: '',
                  servicioAdicional: [],
                  nombre: '',
                  empresa: '',
                  email: '',
                  telefono: ''
                });
              }}
              className="w-full bg-green-600 hover:bg-green-700 text-white py-3 rounded-xl font-semibold transition-colors duration-300"
            >
              Enviar Nueva Cotización
            </button>
            
            <button
              onClick={() => window.location.href = '/'}
              className="w-full border border-gray-300 text-gray-700 hover:bg-gray-50 py-3 rounded-xl font-semibold transition-colors duration-300"
            >
              Volver al Inicio
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="w-20 h-20 bg-gradient-to-br from-green-100 to-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Calculator className="w-10 h-10 text-green-800" />
          </div>
          <h1 className="text-4xl font-bold text-green-900 mb-4">Solicita tu Cotización</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Obtén un presupuesto personalizado para tus necesidades de logística internacional
          </p>
          <div className="mt-4 text-sm text-gray-500">
            📧 Se enviará directamente a: <span className="font-semibold text-green-600">contacto@rezcom-intl.mx</span>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <motion.div
            className="lg:col-span-2"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <form onSubmit={handleSubmit} className="space-y-8">
                <div>
                  <h3 className="text-xl font-semibold text-green-900 mb-4 flex items-center">
                    <Truck className="w-6 h-6 mr-2" />
                    Detalles del Envío
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tipo de Servicio *
                      </label>
                      <select
                        name="tipoServicio"
                        value={formData.tipoServicio}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        required
                      >
                        <option value="">Selecciona un servicio</option>
                        {tiposServicio.map(tipo => (
                          <option key={tipo.value} value={tipo.value}>
                            {tipo.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Fecha de Envío *
                      </label>
                      <input
                        type="date"
                        name="fechaEnvio"
                        value={formData.fechaEnvio}
                        onChange={handleChange}
                        min={new Date().toISOString().split('T')[0]}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Ciudad de Origen *
                      </label>
                      <input
                        type="text"
                        name="origen"
                        value={formData.origen}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="Ciudad, País"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Ciudad de Destino *
                      </label>
                      <input
                        type="text"
                        name="destino"
                        value={formData.destino}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="Ciudad, País"
                        required
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-green-900 mb-4 flex items-center">
                    <Package className="w-6 h-6 mr-2" />
                    Información de la Mercancía
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Peso (kg) *
                      </label>
                      <input
                        type="number"
                        name="peso"
                        value={formData.peso}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="0"
                        min="1"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Dimensiones (LxWxH cm) *
                      </label>
                      <input
                        type="text"
                        name="dimensiones"
                        value={formData.dimensiones}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="100x50x30"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tipo de Mercancía *
                      </label>
                      <select
                        name="tipoMercancia"
                        value={formData.tipoMercancia}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        required
                      >
                        <option value="">Selecciona el tipo</option>
                        <option value="general">Mercancía General</option>
                        <option value="fragil">Mercancía Frágil</option>
                        <option value="perecedero">Productos Perecederos</option>
                        <option value="peligroso">Materiales Peligrosos</option>
                        <option value="electronico">Productos Electrónicos</option>
                        <option value="textil">Productos Textiles</option>
                        <option value="maquinaria">Maquinaria Industrial</option>
                        <option value="automotriz">Partes Automotrices</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Valor Declarado (USD) *
                      </label>
                      <input
                        type="number"
                        name="valor"
                        value={formData.valor}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="0.00"
                        min="1"
                        step="0.01"
                        required
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-green-900 mb-4">
                    Servicios Adicionales
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {serviciosAdicionales.map(servicio => (
                      <label key={servicio.value} className="flex items-center space-x-3 cursor-pointer p-3 rounded-lg hover:bg-gray-50 transition-colors duration-200">
                        <input
                          type="checkbox"
                          value={servicio.value}
                          checked={formData.servicioAdicional.includes(servicio.value)}
                          onChange={handleChange}
                          className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                        />
                        <span className="text-gray-700 font-medium">{servicio.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-green-900 mb-4">
                    Información de Contacto
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nombre Completo *
                      </label>
                      <input
                        type="text"
                        name="nombre"
                        value={formData.nombre}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Empresa
                      </label>
                      <input
                        type="text"
                        name="empresa"
                        value={formData.empresa}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="Opcional"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Teléfono *
                      </label>
                      <input
                        type="tel"
                        name="telefono"
                        value={formData.telefono}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="+52 555 123 4567"
                        required
                      />
                    </div>
                  </div>
                </div>

                <motion.button
                  type="submit"
                  disabled={isLoading}
                  className={`w-full flex items-center justify-center space-x-2 py-4 px-6 rounded-xl font-semibold transition-all duration-300 ${
                    isLoading
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-green-600 to-green-800 hover:from-green-700 hover:to-green-900 text-white shadow-lg hover:shadow-xl transform hover:scale-105'
                  }`}
                  whileHover={{ scale: isLoading ? 1 : 1.05 }}
                  whileTap={{ scale: isLoading ? 1 : 0.95 }}
                >
                  {isLoading ? (
                    <>
                      <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Enviando...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      <span>Enviar Cotización por Email</span>
                    </>
                  )}
                </motion.button>
              </form>
            </div>
          </motion.div>

          <motion.div
            className="space-y-6"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <div className="bg-white rounded-2xl shadow-xl p-6">
              <h3 className="text-xl font-semibold text-green-900 mb-4">
                ¿Necesitas ayuda?
              </h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Phone className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="font-medium text-gray-900">+52 556 215 2214</p>
                    <p className="text-sm text-gray-600">Lun-Vie 8:00-18:00</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="font-medium text-gray-900">contacto@rezcom-intl.mx</p>
                    <p className="text-sm text-gray-600">Respuesta en 24h</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-100 to-blue-100 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-green-900 mb-3">
                Proceso de Cotización
              </h3>
              <div className="space-y-3 text-sm text-gray-700">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                  <span>Tu solicitud llega por email</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                  <span>Analizamos tus necesidades</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                  <span>Te contactamos en 24h</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                  <span>Enviamos cotización detallada</span>
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-yellow-800 mb-3">
                ⚡ Respuesta Inmediata
              </h3>
              <p className="text-yellow-700 text-sm mb-4">
                ¿Necesitas una respuesta más rápida? Envía tu cotización también por WhatsApp.
              </p>
              <button
                type="button"
                onClick={() => {
                  const message = "Hola REZCOM, necesito una cotización urgente para un envío. ¿Podrían ayudarme?";
                  const whatsappURL = `https://wa.me/5215562152214?text=${encodeURIComponent(message)}`;
                  window.open(whatsappURL, '_blank');
                }}
                className="w-full bg-green-500 hover:bg-green-600 text-white py-3 rounded-xl font-semibold transition-colors duration-300"
              >
                📱 WhatsApp Directo
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Cotizacion;